import React, { useState, useEffect, useContext, useMemo } from "react";
import { Typography, Grid, Select, MenuItem, Button, InputLabel, Box } from '@mui/material';
import { Container } from "@mui/system";
import { useSearchParams } from "react-router-dom";
import { Helmet } from 'react-helmet-async';
import { DraftYearContext } from "../../contexts/DraftYearContext";
import { publicApi } from "../../api/http";
import RankMockCard from "../../components/RankMockCard/RankMockCard";
import GridListViewToggle from "../../components/GridListViewToggle/GridListViewToggle";
import "./MocksAndRankings.css";
import "./MediaRankingLayout.css";
import PageLoader from "../../components/PageLoader/PageLoader";


function MocksAndRankings() {
    const [searchParams, setSearchParams] = useSearchParams();
    const [rankMocks, setRankMocks] = useState(null);
    const [scout, setScout] = useState(searchParams.get('scouts_id') || "");
    const [scouts, setScouts] = useState([]);
    const { draftYear, setDraftYear } = useContext(DraftYearContext);
    const [publication, setPublication] = useState(searchParams.get('publication_id') || "");
    const [publications, setPublications] = useState([]);
    const [error, setError] = useState(null);
    const [viewMode, setViewMode] = useState("grid");

    // Loading States
    const [loading, setLoading] = useState(true);
    const [draftYearChange, setDraftYearChange] = useState(true);
    const [prevDraftYear, setPrevDraftYear] = useState(null);

    useEffect(() => {
        setLoading(true);
        publicApi.get("/shared/get_mocks_and_rankings", { params: { "scouts_id": scout, "publication_id": publication, "draft_year": draftYear } }).then((response) => {
            setRankMocks(response.data);
            setLoading(false);
        })
            .catch((error) => {
                setError(error);
            });

    }, [scout, publication, draftYear]);

    useEffect(() => {
        publicApi.get("/scouts").then((response) => {
            setScouts(response.data);
        })
            .catch((error) => {
                setError(error);
            });
    }, []);


    useEffect(() => {
        publicApi.get("/publications").then((response) => {
            setPublications(response.data);
        })
            .catch((error) => {
                setError(error);
            });
    }, []);

    const sortedScouts = useMemo(
        () => [...scouts].sort((a, b) => a.name.localeCompare(b.name)),
        [scouts]
    );

    const sortedPublications = useMemo(
        () => [...publications].sort((a, b) => a.name.localeCompare(b.name)),
        [publications]
    );

    useEffect(() => {
        setPublication(searchParams.get('publication_id') || "");
        setScout(searchParams.get('scouts_id') || "");
    }, [searchParams]);

    const applyFiltersToUrl = (nextScout, nextPublication) => {
        const params = {};
        if (nextScout) params.scouts_id = nextScout;
        if (nextPublication) params.publication_id = nextPublication;
        setSearchParams(params);
    };

    const handleScoutChange = (event) => {
        const nextScout = event.target.value;
        setScout(nextScout);
        applyFiltersToUrl(nextScout, publication);
    };
    const handlePublicationChange = (event) => {
        const nextPublication = event.target.value;
        setPublication(nextPublication);
        applyFiltersToUrl(scout, nextPublication);
    };

    const clearFilters = () => {
        setScout("");
        setPublication("");
        setSearchParams();
    };

    useEffect(() => {
        if (prevDraftYear == null) {
            setPrevDraftYear(draftYear);
        }
        else {
            if (prevDraftYear != draftYear) {
                setDraftYearChange(false);
            }
        }
        setPrevDraftYear(draftYear);
    }, [draftYear]);

    function getIsLoading() {
        return loading && !draftYearChange;
    }

    const menuPaperSx = {
        backgroundColor: 'var(--bg-surface-2)',
        border: '1px solid var(--border-default)',
        borderRadius: '10px',
        boxShadow: 'var(--shadow-lg)',
        mt: 0.5,
        '& .MuiMenuItem-root': {
            fontFamily: "'Inter', sans-serif",
            fontSize: '13.5px',
            color: 'var(--text-primary)',
            borderRadius: '6px',
            margin: '2px 4px',
            transition: 'background-color 0.12s ease',
            '&:hover': { backgroundColor: 'rgba(255,255,255,0.06)' },
            '&.Mui-selected': { backgroundColor: 'rgba(126,168,98,0.15)', color: 'var(--brand-strong)', fontWeight: 600 },
            '&.Mui-selected:hover': { backgroundColor: 'rgba(126,168,98,0.22)' },
        },
    };

    return (
        <>
            <Helmet>
                <title>Media Rankings - Hockey Pipeline</title>
                <meta name="description" content="Browse NHL mock drafts and prospect rankings from scouts and publications. Filter by scout, publication, and draft year." />
                <link rel="canonical" href="https://hockeypipeline.com/media-rankings" />
            </Helmet>
            <PageLoader loading={getIsLoading()} />
            <Container Container className="container top-container">
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={12} md={12} width="100%">
                        <Box className="media-rankings-page-header">
                            <Box>
                                <Typography variant="h4" component="div" className="page-title">
                                    Media Rankings
                                </Typography>
                                <div className="title-underline" />
                            </Box>
                            <GridListViewToggle
                                value={viewMode}
                                onChange={setViewMode}
                                ariaLabel="Media rankings layout"
                            />
                        </Box>
                        <Grid container spacing={2} justifyContent="right">
                            <Grid item xs={12} sm={4} md={3} direction="row">
                                <InputLabel className="drop-down-label">Scout</InputLabel>
                                <Select
                                    value={scout}
                                    autoWidth
                                    labelId="scout-select-label"
                                    id="scout-select"
                                    className="drop-down"
                                    onChange={handleScoutChange}
                                    size="small"
                                    MenuProps={{ PaperProps: { sx: menuPaperSx } }}
                                >
                                    <MenuItem value="">All Scouts</MenuItem>
                                    {sortedScouts.map((scout) => (
                                        <MenuItem key={scout.scouts_id} value={scout.scouts_id}>
                                            {scout.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </Grid>
                            <Grid item xs={12} sm={4} md={3}>
                                <InputLabel className="drop-down-label">Publication</InputLabel>
                                <Select
                                    size="small"
                                    value={publication}
                                    autoWidth
                                    className="drop-down"
                                    onChange={handlePublicationChange}
                                    MenuProps={{ PaperProps: { sx: menuPaperSx } }}
                                >
                                    <MenuItem value="">All Publications</MenuItem>
                                    {sortedPublications.map((publication) => (
                                        <MenuItem key={publication.publication_id} value={publication.publication_id}>
                                            {publication.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </Grid>
                            <Grid item xs={12} sm={4} md={2} className="clear-btn-container" >
                                <Button
                                    variant="outlined"
                                    className="clear-btn"
                                    onClick={clearFilters}
                                    disableRipple
                                >
                                    Clear Filters
                                </Button>
                            </Grid>
                        </Grid>
                        {rankMocks && (
                            <Grid
                                container
                                spacing={viewMode === "grid" ? 2 : 0}
                                className={
                                    viewMode === "grid"
                                        ? "media-rankings-items media-rankings-items--grid"
                                        : "media-rankings-items"
                                }
                                sx={{ mt: 2, width: "100%" }}
                            >
                                {rankMocks.map((rankMock) => (
                                    <Grid
                                        item
                                        xs={12}
                                        sm={viewMode === "grid" ? 6 : 12}
                                        md={viewMode === "grid" ? 4 : 12}
                                        key={`${rankMock.rank_type}-${rankMock.data_point_id}`}
                                    >
                                        <RankMockCard rankMock={rankMock} />
                                    </Grid>
                                ))}
                            </Grid>
                        )}
                    </Grid>
                </Grid>
            </Container>
        </>
    );
};

export default MocksAndRankings;